import { type ReactNode } from 'react';

import { useSite } from '../site-context.js';
import { useTranslation } from '../use-translation.js';

export interface LoadingScreenProps {
  /** Renders nothing when false; callers can also conditionally mount it. */
  readonly visible?: boolean;
  /** Visible caption under the spinner; also the accessible announcement. */
  readonly label?: string;
  /** Cover the viewport instead of the nearest positioned ancestor. */
  readonly fullScreen?: boolean;
  /** Spinner diameter in pixels. */
  readonly size?: number;
  /** Content rendered under the overlay inside a relative wrapper. */
  readonly children?: ReactNode;
  readonly className?: string;
}

const SPINNER_SIZE = 112;

export function LoadingScreen({
  visible = true,
  label,
  fullScreen = false,
  size = SPINNER_SIZE,
  children,
  className = '',
}: LoadingScreenProps): React.JSX.Element | null {
  const site = useSite();
  const { t } = useTranslation();
  const caption = label ?? t('common.pleaseWait');
  const overlay = !visible ? null : (
    <div
      role="status"
      aria-live="polite"
      aria-label={caption}
      className={`${
        fullScreen ? 'fixed' : 'absolute'
      } inset-0 z-999 flex cursor-wait items-center justify-center bg-surface/50 backdrop-blur-[5px] ${className}`}
    >
      <div className="flex flex-col items-center gap-3">
        <div className="relative flex" style={{ width: size, height: size }}>
          <div aria-hidden="true" className="ibe-loading-ring animate-spin size-full" />
          {site.logoUrl !== '' && (
            <img
              src={site.logoUrl}
              alt={site.name}
              className="absolute inset-0 m-auto max-h-1/2 max-w-[70%] object-contain"
            />
          )}
        </div>
        <p className="text-sm font-medium text-foreground">{caption}</p>
      </div>
    </div>
  );

  if (children === undefined) return overlay;

  return (
    <div className="relative">
      {children}
      {overlay}
    </div>
  );
}
